<template>
  <content-card v-if="display === 'result'">
    <template v-slot:header>
      <metrics-result :vitalSign="vitalSign" @removeProfile="removeProfile" />
    </template>
    <template v-slot:description>
      <div class="print-reminder" v-html="t('scan_result.print_reminder', {channels: availableChannels})"></div>
      <div class="d-flex justify-content-center flex-column align-items-center g-24">
        <button type="button" :class="`continue-button bg-fwd-orange`" @click="() => switchDisplay('sendingMethod')">
          {{ t("button.get_report") }}
        </button>
        <button type="button" class="continue-button-clear fwd-orange" @click="removeProfile">
          {{ t("button.try_again") }}
        </button>
      </div>
    </template>
  </content-card>
  <sending-method v-else-if="display === 'sendingMethod'" @chooseMethod="chooseMethod" @back="back" />
  <send-result
    v-else-if="display === 'sendResult'"
    :chosenMethod="chosenMethod"
    :loadingSending="loadingSending"
    :qrCodeLink="qrCodeLink"
    @sendReport="sendReport"
    @back="back"
    @chooseMethod="chooseMethod"
  />
  <send-status
    v-else-if="display === 'sendStatus'"
    :receiverInformation="receiverInformation"
    :sendStatus="sendStatus"
    :sendingMethod="chosenMethod"
    @continue="display = 'result'"
    @back="display = 'sendResult'"
  />
</template>

<script>
import SendResult from "./send-result.vue";
import SendingMethod from "./sending-method.vue";
import MetricsResult from "./metrics-result.vue";
import SendStatus from "./send-status.vue";
import { useI18n } from "vue-i18n";
import sendReport from "../../../common/send-report";
import CryptoJS from "crypto-js";
import { inject } from "vue";

export default {
  setup() {
    const { locale, t } = useI18n();
    const { channels } = inject("channels");
    return {
      locale,
      t,
      channels,
    };
  },
  props: {
    vitalSign: Object,
    userProfile: Object,
    recommendation: String,
  },
  components: {
    MetricsResult,
    SendingMethod,
    SendResult,
    SendStatus,
  },
  emits: ["removeProfile"],
  data() {
    return {
      display: "result",
      chosenMethod: "",
      sendStatus: false,
      receiverInformation: "",
      loadingSending: false,
    };
  },
  computed: {
    qrCodeLink() {
      if (this.chosenMethod == "wechat") {
        let link =
          "https://wechat.client-hub.net/PAI?redirectUrl=REDIRECT_URL";

        const queryValue = this.queryValue;
        let origin = window.location.origin;
        if (origin.includes("localhost")) {
          origin = "https://fwd-aidrinks-uat.panoptic.ai";
        }
        const url = origin + "/#/report?state=" + queryValue;
        const redirectUrl = btoa(url)

        link = link.replace("REDIRECT_URL", redirectUrl);

        return link;
      } else {
        return undefined;
      }
    },
    queryValue() {
      const name = this.userProfile.name,
        vs = this.vitalSign,
        rec = this.recommendation;
      const queryObj = {
        userProfile: {
          name,
        },
        vitalSign: {
          stress: vs.stress,
          generalWellness: vs.generalWellness,
          facialSkinAge: vs.facialSkinAge,
          bloodPressure: vs.bloodPressure,
          datetime: vs.datetime,
        },
        rec,
        locale: this.locale,
      };
      const encryptedQuery = CryptoJS.AES.encrypt(JSON.stringify(queryObj), "FWD_2025").toString();
      const query = encodeURIComponent(encryptedQuery);
      return query;
    },
    availableChannels() {
      let channelText = "";
      this.channels.map((channel, index) => {
        const channelName = this.t('send_report.' + channel + '.name')
        channelText+=channelName
        if (index < this.channels.length - 1) {
          channelText += "/"
        }
      })
      return channelText;
    },
  },
  methods: {
    removeProfile() {
      this.$emit("removeProfile");
    },
    switchDisplay(display) {
      this.display = display;
    },
    chooseMethod(method) {
      if (method !== "") {
        this.display = "sendResult";
      } else {
        this.display = "sendingMethod";
      }
      this.chosenMethod = method;
    },
    back() {
      this.display = "result";
      this.chosenMethod = "";
    },
    sendReport(receiverInformation) {
      this.loadingSending = true;
      switch (this.chosenMethod) {
        case "whatsapp":
          this.receiverInformation = receiverInformation;
          this.sendToWhatsApp();
          break;
        case "wechat":
          this.receiverInformation = "WeChat Account";
          this.loadingSending = false;
          this.sendStatus = "success";
          this.display = "sendStatus";
          break;
        case "email":
          this.receiverInformation = receiverInformation;
          sendReport.exportReport(this.sendToEmail);
          break;
      }
    },
    generateDownloadLink() {
      const url = `/#/report?state=${this.queryValue}`;
      return url;
    },
    async sendToWhatsApp(report) {
      const downloadLink = this.generateDownloadLink();
      try {
        const sendStatus = await sendReport.sendWhatsapp(downloadLink, this.receiverInformation);
        this.sendStatus = sendStatus;
        this.display = "sendStatus";
      } catch (e) {
        console.error(e);
        alert(this.t("message.whatsapp_failed"));
        this.sendingEmail = false;
      }
      this.loadingSending = false;
    },
    async sendToEmail(pdf) {
      try {
        let report = btoa(pdf);
        let emailTemplate = {
          emailContent:
            "多謝您來到 AI 健康應援區！請查看您的健康分析及推薦飲品。\nThanks for visiting the AI Health Support Zone! Here is your health assessment with drinks recommendation",
          emailSubject: "Health Report",
          fileName: "HealthReport.pdf",
        };
        if (this.receiverInformation) {
          const sendStatus = await sendReport.sendEmail(
            process.env.NODE_ENV === "development",
            [this.receiverInformation],
            report,
            emailTemplate,
            "dp.hk@fwd.com"
          );
          this.sendStatus = sendStatus;
          this.display = "sendStatus";
        }
      } catch (e) {
        console.error(e);
        alert(this.t("message.email_failed"));
      }
      this.loadingSending = false;
    },
  },
};
</script>

<style scoped>
.loading-button {
  opacity: 0.3;
}
.loading-indicator {
  width: 20px;
}
.print-reminder {
  font-weight: 500;
  text-align: center;
}
@media (max-width: 767px) {
  .print-reminder {
    font-size: 14px;
  }
}
</style>
